import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import { toast } from 'sonner';
import { Filter, X, XCircle, Inbox, FolderX, TrendingDown } from 'lucide-react';
import { DatePickerField } from '../DatePickerField';
import { useApiCacheRefresh } from '../../lib/apiCache';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const fmtPLN = (v) => `${Math.round(v || 0).toLocaleString('pl-PL')} zł`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('pl-PL') : '–');
const trunc = (s, n = 28) => (s.length > n ? `${s.slice(0, n)}…` : s);

const StatCard = ({ icon: Icon, label, value, color, testId }) => (
  <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4" data-testid={testId}>
    <div className="flex items-center gap-3">
      <div className="p-2 rounded-lg" style={{ background: `${color}22` }}><Icon className="w-5 h-5" style={{ color }} /></div>
      <div>
        <p className="text-xs text-[var(--crm-text-secondary)]">{label}</p>
        <p className="text-xl font-bold text-[var(--crm-text-primary)] font-mono">{value}</p>
      </div>
    </div>
  </Card>
);

const ReasonsChart = ({ reasons }) => {
  const chartData = reasons.slice(0, 10).map((r) => ({ name: trunc(r.reason), count: r.count, full: r.reason }));
  return (
    <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4" data-testid="rejections-chart">
      <h3 className="text-[var(--crm-text-primary)] font-semibold mb-4">Top 10 powodów odrzuceń</h3>
      <ResponsiveContainer width="100%" height={Math.max(200, chartData.length * 42)}>
        <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 40 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--crm-border)" horizontal={false} />
          <XAxis type="number" allowDecimals={false} stroke="var(--crm-text-muted)" fontSize={12} />
          <YAxis type="category" dataKey="name" width={200} stroke="var(--crm-text-muted)" fontSize={12} />
          <Tooltip
            contentStyle={{ background: 'var(--crm-bg-dialog)', border: '1px solid var(--crm-border)', borderRadius: 8 }}
            labelFormatter={(_, p) => p?.[0]?.payload?.full || ''}
            formatter={(v) => [v, 'Liczba']}
          />
          <Bar dataKey="count" fill="#EF4444" radius={[0, 4, 4, 0]}>
            <LabelList dataKey="count" position="right" fill="var(--crm-text-secondary)" fontSize={12} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
};

const ReasonsTable = ({ reasons }) => (
  <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] overflow-x-auto" data-testid="rejections-table">
    <Table>
      <TableHeader>
        <TableRow className="border-[var(--crm-border)]">
          {['#', 'Powód', 'Liczba', 'Udział', 'Zlecenia', 'Skrzynka', 'Utracona wartość'].map((h) => (
            <TableHead key={h} className="text-[var(--crm-text-secondary)] whitespace-nowrap">{h}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {reasons.map((r, i) => (
          <TableRow key={r.reason} className="border-[var(--crm-border)]" data-testid={`rejection-reason-row-${i}`}>
            <TableCell className="text-[var(--crm-text-muted)]">{i + 1}</TableCell>
            <TableCell className="text-[var(--crm-text-primary)] font-medium max-w-md">{r.reason}</TableCell>
            <TableCell className="text-[var(--crm-text-primary)] font-mono font-bold">{r.count}</TableCell>
            <TableCell className="min-w-[140px]">
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 rounded-full bg-[var(--crm-bg-panel)] overflow-hidden">
                  <div className="h-full bg-red-500" style={{ width: `${r.percent}%` }} />
                </div>
                <span className="text-xs text-[var(--crm-text-secondary)] font-mono w-12 text-right">{r.percent}%</span>
              </div>
            </TableCell>
            <TableCell className="text-[var(--crm-text-secondary)] font-mono">{r.projects}</TableCell>
            <TableCell className="text-[var(--crm-text-secondary)] font-mono">{r.inbox}</TableCell>
            <TableCell className="text-red-400 font-mono">{r.lost_value ? fmtPLN(r.lost_value) : '–'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </Card>
);

const RecentList = ({ recent }) => (
  <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4" data-testid="rejections-recent">
    <h3 className="text-[var(--crm-text-primary)] font-semibold mb-3">Ostatnie odrzucenia</h3>
    <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
      {recent.map((r) => (
        <div key={`${r.source}-${r.id}`} className="p-2.5 rounded-md bg-[var(--crm-bg-panel)] border border-[var(--crm-border)]">
          <div className="flex items-center justify-between gap-2 text-xs">
            <span className="text-[var(--crm-text-primary)] font-medium truncate">{r.name || 'Bez nazwy'}</span>
            <span className="text-[var(--crm-text-muted)] shrink-0">{fmtDate(r.date)} · {r.initials}</span>
          </div>
          <p className="text-xs text-[var(--crm-text-secondary)] mt-1">
            <span className={`mr-1.5 px-1.5 py-0.5 rounded text-[10px] ${r.source === 'inbox' ? 'bg-sky-500/15 text-sky-400' : 'bg-red-500/15 text-red-400'}`}>
              {r.source === 'inbox' ? 'Skrzynka' : 'Zlecenie'}
            </span>
            {r.reason || 'Brak podanej przyczyny'}
          </p>
        </div>
      ))}
    </div>
  </Card>
);

export const RejectionsTab = ({ usersList = [], canFilterUsers }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [userId, setUserId] = useState('all');

  const fetchData = useCallback(async (silent) => {
    if (silent !== true) setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (userId !== 'all') params.user_id = userId;
      const res = await axios.get(`${API_URL}/reports/rejections`, { params });
      setData(res.data);
    } catch { toast.error('Błąd pobierania statystyki odrzuceń'); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo, userId]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useApiCacheRefresh(() => fetchData(true));

  const hasFilters = dateFrom || dateTo || userId !== 'all';

  return (
    <div className="space-y-6" data-testid="rejections-tab">
      <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4">
        <div className="flex flex-wrap items-end gap-4">
          <Filter className="w-4 h-4 text-[var(--crm-text-secondary)] shrink-0 mb-2" />
          <div>
            <label className="text-[var(--crm-text-secondary)] text-xs block mb-1">Od</label>
            <DatePickerField value={dateFrom} onChange={setDateFrom} placeholder="Data od" data-testid="rejections-date-from" />
          </div>
          <div>
            <label className="text-[var(--crm-text-secondary)] text-xs block mb-1">Do</label>
            <DatePickerField value={dateTo} onChange={setDateTo} placeholder="Data do" data-testid="rejections-date-to" />
          </div>
          {canFilterUsers && (
            <div className="min-w-[200px]">
              <label className="text-[var(--crm-text-secondary)] text-xs block mb-1">Handlowiec</label>
              <Select value={userId} onValueChange={setUserId}>
                <SelectTrigger className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" data-testid="rejections-user-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)]">
                  <SelectItem value="all" className="text-[var(--crm-text-primary)]">Wszyscy</SelectItem>
                  {usersList.map((u) => (
                    <SelectItem key={u.id} value={u.id} className="text-[var(--crm-text-primary)]">{u.initials} – {u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {hasFilters && (
            <Button size="sm" variant="ghost" data-testid="rejections-clear-filters"
              onClick={() => { setDateFrom(''); setDateTo(''); setUserId('all'); }}
              className="text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)] mb-0.5">
              <X className="w-4 h-4 mr-1" />Wyczyść
            </Button>
          )}
        </div>
      </Card>

      {loading ? (
        <div className="text-center text-[var(--crm-text-secondary)] py-12">Ładowanie...</div>
      ) : !data || data.total === 0 ? (
        <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-12 text-center" data-testid="rejections-empty">
          <XCircle className="w-10 h-10 text-[var(--crm-text-muted)] mx-auto mb-3" />
          <p className="text-[var(--crm-text-secondary)]">Brak odrzuceń w wybranym okresie</p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={XCircle} label="Wszystkie odrzucenia" value={data.total} color="#EF4444" testId="rejections-total" />
            <StatCard icon={FolderX} label="Odrzucone zlecenia" value={data.total_projects} color="#F97316" testId="rejections-total-projects" />
            <StatCard icon={Inbox} label="Odrzucone ze skrzynki" value={data.total_inbox} color="#0EA5E9" testId="rejections-total-inbox" />
            <StatCard icon={TrendingDown} label="Utracona wartość ofert" value={fmtPLN(data.lost_value)} color="#A855F7" testId="rejections-lost-value" />
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2"><ReasonsChart reasons={data.reasons} /></div>
            <RecentList recent={data.recent} />
          </div>
          <ReasonsTable reasons={data.reasons} />
        </>
      )}
    </div>
  );
};

export default RejectionsTab;
